import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import Select from '@decisiv/ui-components/lib/components/Select';
import Button from '@decisiv/ui-components/lib/components/Button';
import { spacing } from '@decisiv/design-tokens';
import Search from '@decisiv/iconix/lib/components/Search';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TIME_RANGE_OPTIONS = [
  { id: '1h', label: '1 hour', value: '1h' },
  { id: '4h', label: '4 hours', value: '4h' },
  { id: '12h', label: '12 hours', value: '12h' },
  { id: '24h', label: '24 hours', value: '24h' },
  { id: '3d', label: '3 days', value: '3d' },
  { id: '7d', label: '7 days', value: '7d' },
];

function SearchForm({
  onSearch,
  loading,
  initialErrorId,
  initialTraceId,
  initialTimeRange,
}) {
  const [errorId, setErrorId] = useState(initialErrorId || '');
  const [traceId, setTraceId] = useState(initialTraceId || '');
  const [timeRange, setTimeRange] = useState(initialTimeRange || '24h');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = useCallback(() => {
    const trimmedErrorId = errorId.trim();

    if (!trimmedErrorId) {
      setValidationError('Error ID is required.');
      return;
    }

    if (!UUID_REGEX.test(trimmedErrorId)) {
      setValidationError(
        'Enter a valid UUID format (e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890).',
      );
      return;
    }

    setValidationError('');
    onSearch({
      errorId: trimmedErrorId,
      traceId: traceId.trim(),
      timeRange,
    });
  }, [errorId, traceId, timeRange, onSearch]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleErrorIdChange = useCallback(
    (e) => {
      setErrorId(e.target.value);
      if (validationError) setValidationError('');
    },
    [validationError],
  );

  const handleTraceIdChange = useCallback((e) => {
    setTraceId(e.target.value);
  }, []);

  const handleTimeRangeChange = useCallback((selectedId) => {
    setTimeRange(selectedId);
  }, []);

  return (
    <Flex flexDirection="column">
      <Flex alignItems="flex-start">
        <Flex flex={1} marginRight={1}>
          <TextField
            label="Error ID"
            name="error_id"
            value={errorId}
            onChange={handleErrorIdChange}
            onKeyDown={handleKeyDown}
            required
            placeholder="a1b2c3d4-e5f6-7890-abcd-ef1234567890"
            warningMessage={validationError}
            helpMessage="UUID from the error page"
          />
        </Flex>

        <Flex flex={1} marginRight={1}>
          <TextField
            label="Trace ID"
            name="trace_id"
            value={traceId}
            onChange={handleTraceIdChange}
            onKeyDown={handleKeyDown}
            placeholder="6aa06477000000000bef57f1e5d87355"
            helpMessage="Optional. Narrows results to a single request."
          />
        </Flex>

        <Flex style={{ width: '160px', flexShrink: 0 }} marginRight={1}>
          <Select
            label="Time Range"
            name="time_range"
            options={TIME_RANGE_OPTIONS}
            value={timeRange}
            onChange={handleTimeRangeChange}
            hideClearButton
          />
        </Flex>

        <Flex style={{ flexShrink: 0, marginBottom: spacing.base * 2 }}>
          <Button
            icon={Search}
            text="Search"
            kind="primary"
            onClick={handleSubmit}
            loading={loading}
            loadingText="Searching..."
            disabled={loading}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

SearchForm.propTypes = {
  onSearch: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  initialErrorId: PropTypes.string,
  initialTraceId: PropTypes.string,
  initialTimeRange: PropTypes.string,
};

SearchForm.defaultProps = {
  loading: false,
  initialErrorId: '',
  initialTraceId: '',
  initialTimeRange: '24h',
};

export default SearchForm;
